import _ from "lodash";
import React, { useState } from "react";
import classes from "./List.scss";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";
import withLoader from "UIComponents/GqlWithLoader";
import { ArrowIcon } from "SvgComponents";
import { LIST_POPUP_DIRECTION, colors } from "Constants";
import UserNameAvatar from "AppComponents/UserNameAvatar";
import { Tooltip } from "AppComponents/ArchivedComponents";
import classNames from "classnames";
import { GENERIC_TAGS_INFO } from "modules/Services";
import { checkScrollAtBottom } from "services";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { Tag, Tooltip as DSTooltip } from "@toddle-edu/ds-web";

const styles = {
  avatarStyles: { randomizeColor: true, shade: "light" },
  customSubTextStyle: { display: "flex" },
  tagLabelStyle: { padding: "0px 2px" },
  tooltipContainerStyle: { padding: "0px" },
};

const defaultSquareAvatarStyles = {
  randomizeColor: true,
  shade: "light",
  shape: "square",
};

const defaultOverrideStyles = {
  squareAvatarStyles: defaultSquareAvatarStyles,
};

const MemberList = React.memo(props => {
  const {
    items,
    isLoadingMore,
    keyToMap,
    userType,
    showCourseRole,
    showSquareAvatar,
    showTooltip: showSingleTooltip = false,
    tooltipPlacement,
    dir,
    getSubText,
    overrideStyles = defaultOverrideStyles,
    dsVersion,
  } = props;

  const [showTooltip, setShowTooltip] = useState(true);
  const toggleTooltip = () => {
    if (showSingleTooltip) setShowTooltip(prev => !prev);
  };

  return (
    <div className={classes.listInnerContainer}>
      {_.map(items, item => {
        const { id, firstName, isArchived, alertTypes = [], type } = item;
        const gradesList = item.grades;

        let displayName;
        if (keyToMap) {
          displayName = _.get(item, keyToMap);
        } else {
          displayName = firstName
            ? getFormattedUserName({ user: item, type: userType })
            : item.title
            ? item.title
            : !_.isObject(item)
            ? _.toString(item)
            : "";
        }

        const subtextComponent =
          item.grades || showCourseRole ? (
            <div
              className={classes.subTextContainer}
              dir={dir}
              onMouseEnter={toggleTooltip}
              onMouseLeave={toggleTooltip}
            >
              {item.grades ? (
                <Tooltip
                  data={gradesList}
                  tooltipPlacement={"bottom"}
                  length={showCourseRole ? 1 : 2}
                  keyToMap={"name"}
                  trigger={["hover"]}
                />
              ) : null}

              {item.grades && item.roleSubtext ? (
                <div className="text-body-s"> • </div>
              ) : null}

              {showCourseRole && item.roleSubtext ? (
                <DSTooltip tooltip={item.roleSubtext}>
                  <div className={classNames("text-body-s", classes.roleText)}>
                    {item.roleSubtext}
                  </div>
                </DSTooltip>
              ) : null}
            </div>
          ) : null;

        const subText = _.isFunction(getSubText)
          ? getSubText(item)
          : subtextComponent;

        const singleRowClass = classNames({
          [classes.rowContainer]: true,
          [classes.archivedCell]: item?.isArchived,
        });

        const defaultTagColor = item?.tagColor || "teal";

        return (
          <div className={singleRowClass} key={id}>
            <div className={classes.nameAndDetailContainer}>
              <UserNameAvatar
                id={id}
                profileImage={item.profileImageData?.url || item.profileImage}
                name={displayName}
                viewMode={"NAME_AND_CUSTOM_SUBTEXT"}
                shouldDynamicColor={true}
                isArchived={isArchived}
                showAvatar={true}
                avatarStyles={
                  showSquareAvatar
                    ? overrideStyles.squareAvatarStyles ||
                      defaultSquareAvatarStyles
                    : styles.avatarStyles
                }
                showArchivedText={true}
                showTooltip={showTooltip}
                tooltipPlacement={tooltipPlacement}
                customSubText={subText}
                customSubTextStyle={styles.customSubTextStyle}
                tooltipContainerStyle={styles.tooltipContainerStyle}
                profileCellStyle={overrideStyles.profileCellStyle}
                profileCascadeDsVersion={dsVersion}
                dir={dir}
                studentAlertsConfig={
                  type === "student"
                    ? {
                        alertTypes,
                        studentId: id,
                      }
                    : undefined
                }
              />
            </div>
            {item?.tagLabel ? (
              <div className={classes.iconButton}>
                <span style={styles.tagLabelStyle}>
                  <Tag
                    _legacy_size={"x-small"}
                    size="small"
                    color={_.get(
                      GENERIC_TAGS_INFO,
                      `${item?.tagLabel}.color`,
                      defaultTagColor
                    )}
                  >
                    {item?.tagLabel}
                  </Tag>
                </span>
              </div>
            ) : null}
          </div>
        );
      })}
      {isLoadingMore ? (
        <div className={classes.loader}>
          <Loading />
        </div>
      ) : null}
    </div>
  );
});

MemberList.displayName = "MemberList";

const MemberListWithLoader = withLoader(MemberList);

class List extends React.PureComponent {
  constructor(props) {
    super(props);
    this.scrollContainerRef = React.createRef(null);
  }

  componentDidMount() {
    if (this.scrollContainerRef.current) {
      this.scrollContainerRef.current.focus({ preventScroll: true });
    }
  }

  onRemoveClick = ({ item }) => {
    const { onRemoveClick, id } = this.props;
    onRemoveClick({ id, item });
  };

  handleScroll = e => {
    const { loadMoreFeed, hasNextPage, isLoadingMore } = this.props;
    const containerDiv = e.target;

    if (
      containerDiv &&
      hasNextPage &&
      !isLoadingMore &&
      checkScrollAtBottom(containerDiv)
    ) {
      loadMoreFeed?.();
    }
  };

  render() {
    const {
      value,
      t,
      isEditMode,
      gradesName,
      isStudentArchived,
      direction,
      hasNextPage,
      isLoading = false,
      isData = true,
      isLoadingMore,
      loadMoreFeed,
      keyToMap,
      userType,
      showCourseRole,
      showSquareAvatar,
      showTooltip = false,
      tooltipPlacement = "top",
      dir,
      overrideStyles,
      getSubText,
      dsVersion,
      overrideClasses = {},
    } = this.props;

    const isRight = _.isEqual(LIST_POPUP_DIRECTION.RIGHT, direction);

    return (
      <div
        className={classNames(classes.container, {
          [classes.containerRight]: isRight,
        })}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        role="presentation"
      >
        <div
          className={classNames(classes.arrowContainer, {
            [classes.arrowContainerRight]: isRight,
          })}
        >
          <ArrowIcon fill={colors.white} width={20} height={10} />
        </div>
        <div className={classes.innerContainer}>
          <div
            className={classNames(
              classes.listContainer,
              overrideClasses.listContainer,
              {
                [classes.listContainerRight]: isRight,
              }
            )}
            onScroll={loadMoreFeed ? this.handleScroll : null}
            ref={this.scrollContainerRef}
            tabIndex={-1}
          >
            <MemberListWithLoader
              items={value}
              onRemoveClick={this.onRemoveClick}
              gradesName={gradesName}
              t={t}
              isEditMode={isEditMode}
              isStudentArchived={isStudentArchived}
              hasNextPage={hasNextPage}
              isData={isData}
              isLoading={isLoading}
              isLoadingMore={isLoadingMore}
              loadMoreFeed={loadMoreFeed}
              keyToMap={keyToMap}
              userType={userType}
              showCourseRole={showCourseRole}
              showSquareAvatar={showSquareAvatar}
              showTooltip={showTooltip}
              tooltipPlacement={tooltipPlacement}
              dir={dir}
              overrideStyles={overrideStyles}
              getSubText={getSubText}
              dsVersion={dsVersion}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default I18nHOC({ resource: ["schoolSetup", "common"] })(List);
